import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, TrophyIcon, BookOpenIcon, ArrowRightIcon, LockIcon, ZapIcon, ShieldCheckIcon, CreditCardIcon, ClockIcon } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
type PlanId = 'basic' | 'premium';
const plans = {
  basic: {
    id: 'basic' as const,
    icon: BookOpenIcon,
    name: 'MEGAPACK KINESIO PRO',
    description: '6 tomos + 4 bonos estratégicos',
    priceOriginal: '$34.990',
    price: '$19.990',
    cta: 'SÍ, QUIERO EL MEGAPACK',
    checkoutUrl: 'https://checkout.tusbeneficios.shop/cart/51145454747881:1'
  },
  premium: {
    id: 'premium' as const,
    icon: TrophyIcon,
    name: 'PACK COMPLETO',
    description: '6 tomos + 4 bonos + Pro Élite',
    priceOriginal: '$43.990',
    price: '$27.990',
    savings: '$15.000',
    cta: 'SÍ, QUIERO EL PACK COMPLETO',
    checkoutUrl: 'https://checkout.tusbeneficios.shop/cart/51145458319593:1'
  }
};
export function PlanComparison() {
  const [selected, setSelected] = useState<PlanId>('premium');
  const currentPlan = plans[selected];
  return <section id="pricing" className="py-14 md:py-24 bg-brand-dark text-white" data-id="element-224">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-225">
        {/* Countdown urgency */}
        <div className="mb-8 md:mb-10" data-id="element-226">
          <div className="flex items-center justify-center gap-2 mb-4 text-brand-accent" data-id="element-227">
            <ClockIcon className="w-4 h-4 md:w-5 md:h-5 animate-pulse" data-id="element-228" />
            <p className="text-xs md:text-sm font-bold uppercase tracking-wider" data-id="element-229">
              Esta oferta termina en
            </p>
          </div>
          <CountdownTimer data-id="element-230" />
        </div>

        {/* Heading */}
        <div className="text-center mb-8" data-id="element-231">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-3 uppercase tracking-tight" data-id="element-232">
            Elegí tu <span className="text-brand-accent" data-id="element-233">pack profesional</span>
          </h2>
          <p className="text-slate-400 text-sm md:text-base" data-id="element-234">
            Ambas opciones incluyen los 4 bonos estratégicos completos
          </p>
        </div>

        {/* Plan cards */}
        <div className="space-y-4 mb-8" data-id="element-235">
          {/* Basic */}
          <PlanCard plan={plans.basic} isSelected={selected === 'basic'} onSelect={() => setSelected('basic')} data-id="element-236" />

          {/* Premium - highlighted */}
          <PlanCard plan={plans.premium} isSelected={selected === 'premium'} onSelect={() => setSelected('premium')} highlighted data-id="element-237" />
        </div>

        {/* Big CTA */}
        <motion.button key={selected} initial={{
        scale: 0.98
      }} animate={{
        scale: 1
      }} whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.99
      }} onClick={() => {
        window.location.href = currentPlan.checkoutUrl;
      }} className="w-full bg-brand-accent hover:bg-brand-accentHover text-white font-extrabold text-base md:text-lg uppercase tracking-wide py-5 rounded-xl shadow-[0_8px_24px_rgba(6,182,212,0.35)] flex items-center justify-center gap-3 transition-colors cursor-pointer" data-id="element-238">
          {currentPlan.cta}
          <ArrowRightIcon className="w-5 h-5" data-id="element-239" />
        </motion.button>

        {/* Reassurance line */}
        <p className="text-center text-slate-400 text-xs md:text-sm italic mt-5 mb-8" data-id="element-240">
          La opción más elegida por quienes buscan resultados reales en su
          postura
        </p>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-3 text-xs md:text-sm text-slate-400 pt-6 border-t border-slate-800" data-id="element-241">
          <TrustBadge icon={LockIcon} label="Pago seguro" data-id="element-242" />
          <TrustBadge icon={ZapIcon} label="Acceso inmediato" data-id="element-243" />
          <TrustBadge icon={ShieldCheckIcon} label="Garantía 30 días" data-id="element-244" />
          <div className="w-full flex justify-center mt-1" data-id="element-245">
            <TrustBadge icon={CreditCardIcon} label="Astro Pay" data-id="element-246" />
          </div>
        </div>
      </div>
    </section>;
}
interface Plan {
  id: PlanId;
  icon: React.ElementType;
  name: string;
  description: string;
  priceOriginal: string;
  price: string;
  savings?: string;
  cta: string;
  checkoutUrl: string;
}
function PlanCard({
  plan,
  isSelected,
  onSelect,
  highlighted = false
}: {
  plan: Plan;
  isSelected: boolean;
  onSelect: () => void;
  highlighted?: boolean;
}) {
  const Icon = plan.icon;
  return <motion.button onClick={onSelect} whileTap={{
    scale: 0.99
  }} className={`w-full text-left rounded-2xl border-2 p-4 md:p-5 transition-all duration-200 relative ${isSelected ? 'border-brand-accent bg-slate-800/60 shadow-[0_0_0_4px_rgba(6,182,212,0.15)]' : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'}`} aria-pressed={isSelected} data-id="element-247">
      <div className="flex items-start gap-3 md:gap-4" data-id="element-248">
        {/* Radio indicator */}
        <div className={`flex-shrink-0 mt-1 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-brand-accent bg-brand-accent' : 'border-slate-500'}`} data-id="element-249">
          {isSelected && <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-white" data-id="element-250" />}
        </div>

        {/* Content */}
        <div className="flex-grow min-w-0" data-id="element-251">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-252">
            <Icon className={`w-4 h-4 md:w-5 md:h-5 ${highlighted ? 'text-brand-accent' : 'text-slate-400'}`} data-id="element-253" />
            <h3 className="font-extrabold text-sm md:text-base uppercase tracking-wide text-white" data-id="element-254">
              {plan.name}
            </h3>
          </div>

          {/* Badges row for highlighted plan */}
          {highlighted && <div className="flex flex-wrap items-center gap-1.5 mb-2" data-id="element-255">
              <span className="bg-brand-accent text-white text-[10px] md:text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide" data-id="element-256">
                ⭐ Más elegido
              </span>
              {plan.savings && <span className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] md:text-xs font-bold px-2 py-0.5 rounded" data-id="element-257">
                  AHORRÁS {plan.savings}
                </span>}
            </div>}

          <p className="text-slate-400 text-xs md:text-sm leading-snug" data-id="element-258">
            {plan.description}
          </p>
        </div>

        {/* Price */}
        <div className="flex-shrink-0 text-right" data-id="element-259">
          <div className="text-slate-500 text-xs md:text-sm line-through leading-tight" data-id="element-260">
            {plan.priceOriginal}
          </div>
          <div className={`text-lg md:text-2xl font-black leading-tight ${highlighted ? 'text-brand-accent' : 'text-white'}`} data-id="element-261">
            {plan.price}
          </div>
        </div>
      </div>
    </motion.button>;
}
function TrustBadge({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <div className="flex items-center gap-1.5" data-id="element-262">
      <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-brand-accent" data-id="element-263" />
      <span className="font-medium" data-id="element-264">{label}</span>
    </div>;
}